import { io, Socket } from 'socket.io-client';

// ----------- Type Definitions -----------
export interface SocketEvent {
    event: string;
    data: any;
    timestamp: number;
}

export interface SocketServiceConfig {
    url: string;
    options?: any;
    autoConnect?: boolean;
    reconnection?: boolean;
    reconnectionAttempts?: number;
    reconnectionDelay?: number;
}

export interface SocketServiceInterface {
    connect(): void;
    disconnect(): void;
    emit(event: string, data?: any): void;
    on(event: string, callback: (data: any) => void): void;
    off(event: string, callback?: (data: any) => void): void;
    isConnected(): boolean;
    getEventHistory(): SocketEvent[];
    clearEventHistory(): void;
}

// ----------- Base Socket Service Class -----------
export abstract class BaseSocketService implements SocketServiceInterface {
    protected socket: Socket | null = null;
    protected eventHistory: SocketEvent[] = [];
    protected eventListeners: Map<string, Set<(data: any) => void>> = new Map();
    protected config: SocketServiceConfig;
    protected isConnecting: boolean = false;
    protected reconnectAttempts: number = 0;
    protected maxReconnectAttempts: number = 5;
    protected connectionTimeout: NodeJS.Timeout | null = null;

    constructor(config: SocketServiceConfig) {
        this.config = config;
    }

    // Connect to socket
    connect(): void {
        if (this.socket?.connected) {
            return;
        }

        if (this.isConnecting) {
            return;
        }

        this.isConnecting = true;

        try {
            // Get the global socket from the manager instead of creating a new one
            const globalSocket = socketServiceManager.getGlobalSocket();
            
            if (globalSocket) {
                this.socket = globalSocket;
                this.setupEventListeners();
                this.isConnecting = false;
                return;
            }

            // If no global socket exists, initialize it
            console.log('🔌 No global socket found, initializing...');
            socketServiceManager.initializeGlobalSocket(this.config);
            
            // Wait a bit for the global socket to be created
            setTimeout(() => {
                const newGlobalSocket = socketServiceManager.getGlobalSocket();
                if (newGlobalSocket) {
                    console.log('🔌 Using newly created global socket');
                    this.socket = newGlobalSocket;
                    this.setupEventListeners();
                } else {
                    this.handleConnectionError(new Error('Global socket initialization failed'));
                }
                this.isConnecting = false;
            }, 100);

        } catch (error) {
            this.isConnecting = false;
            this.handleConnectionError(error);
        }
    }

    // Disconnect from socket
    disconnect(): void {
        if (this.connectionTimeout) {
            clearTimeout(this.connectionTimeout);
            this.connectionTimeout = null;
        }

        if (this.socket) {
            // Don't disconnect the global socket, just remove our event listeners
            this.eventListeners.forEach((callbacks, event) => {
                callbacks.forEach(callback => {
                    this.socket!.off(event, callback);
                });
            });
            this.eventListeners.clear();
            this.socket = null;
            this.isConnecting = false;
            this.reconnectAttempts = 0;
        }
    }

    // Emit event with better error handling
    emit(event: string, data?: any): void {
        if (!this.socket) {
            // Only queue subscribe events, not unsubscribe (unsubscribe should fail fast)
            if (event === 'subscribe') {
                this.queueEventForLater(event, data);
            } else {
                console.warn(`⚠️ Cannot emit '${event}' - socket not initialized`);
            }
            return;
        }

        if (this.socket.connected) {
            try {
                this.socket.emit(event, data);
                this.addEventToHistory(`emit:${event}`, data);
            } catch (error) {
                // Only queue subscribe events on error, not unsubscribe
                if (event === 'subscribe') {
                    this.queueEventForLater(event, data);
                } else {
                    console.error(`❌ Error emitting '${event}':`, error);
                }
            }
        } else {
            // Only queue subscribe events when disconnected, not unsubscribe
            // Unsubscribe events should fail fast if socket is disconnected
            if (event === 'subscribe') {
                this.queueEventForLater(event, data);
            } else {
                console.warn(`⚠️ Cannot emit '${event}' - socket not connected`);
            }
        }
    }

    // Listen to event with validation
    on(event: string, callback: (data: any) => void): void {
        if (!event || typeof event !== 'string') {
            return;
        }

        if (typeof callback !== 'function') {
            return;
        }

        if (!this.eventListeners.has(event)) {
            this.eventListeners.set(event, new Set());
        }
        this.eventListeners.get(event)!.add(callback);

        if (this.socket) {
            this.socket.on(event, (data) => {
                try {
                    callback(data);
                } catch (error) {
                }
            });
        }
    }

    // Remove event listener
    off(event: string, callback?: (data: any) => void): void {
        if (callback) {
            this.eventListeners.get(event)?.delete(callback);
            if (this.socket) {
                this.socket.off(event, callback);
            }
        } else {
            this.eventListeners.delete(event);
            if (this.socket) {
                this.socket.off(event);
            }
        }
    }

    // Check if connected
    isConnected(): boolean {
        return this.socket?.connected ?? false;
    }

    // Get event history
    getEventHistory(): SocketEvent[] {
        return [...this.eventHistory];
    }

    // Clear event history
    clearEventHistory(): void {
        this.eventHistory = [];
    }

    // Add event to history
    protected addEventToHistory(event: string, data: any): void {
        const socketEvent: SocketEvent = {
            event,
            data,
            timestamp: Date.now(),
        };
        this.eventHistory = [...this.eventHistory.slice(-99), socketEvent]; // Keep last 100 events
    }

    // Queue event for later emission when socket reconnects
    private queuedEvents: Array<{ event: string; data: any }> = [];
    
    private queueEventForLater(event: string, data: any): void {
        this.queuedEvents.push({ event, data });
        // Keep only last 50 queued events
        if (this.queuedEvents.length > 50) {
            this.queuedEvents = this.queuedEvents.slice(-50);
        }
    }

    // Emit queued events when socket reconnects
    private emitQueuedEvents(): void {
        if (this.queuedEvents.length === 0) return;

        console.log(`📤 Emitting ${this.queuedEvents.length} queued events...`);
        while (this.queuedEvents.length > 0) {
            const queuedEvent = this.queuedEvents.shift();
            if (queuedEvent && this.socket?.connected) {
                try {
                    this.socket.emit(queuedEvent.event, queuedEvent.data);
                    this.addEventToHistory(`emit:${queuedEvent.event}`, queuedEvent.data);
                } catch (error) {
                    console.error(`❌ Error emitting queued event '${queuedEvent.event}':`, error);
                }
            }
        }
    }

    // Handle connection errors
    protected handleConnectionError(error: any): void {
        this.addEventToHistory('connection_error', { error: error.message || error });
        
        if (this.connectionTimeout) {
            clearTimeout(this.connectionTimeout);
            this.connectionTimeout = null;
        }

        this.isConnecting = false;
        
        if (this.config.reconnection && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            console.log(`🔄 Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
            
            setTimeout(() => {
                this.connect();
            }, this.config.reconnectionDelay || 1000);
        } else {
        }
    }

    // Setup event listeners
    private setupEventListeners(): void {
        if (!this.socket) return;

        // Connection events
        this.socket.on('connect', () => {
            this.reconnectAttempts = 0;
            this.isConnecting = false;
            
            if (this.connectionTimeout) {
                clearTimeout(this.connectionTimeout);
                this.connectionTimeout = null;
            }
            
            this.addEventToHistory('connect', { timestamp: Date.now() });
            this.emitQueuedEvents(); // Emit any queued events
        });

        this.socket.on('disconnect', (reason: string) => {
            this.isConnecting = false;
            this.addEventToHistory('disconnect', { reason, timestamp: Date.now() });
        });

        this.socket.on('connect_error', (error: any) => {
            this.handleConnectionError(error);
        });

        this.socket.on('reconnect', (attemptNumber: number) => {
            this.addEventToHistory('reconnect', { attemptNumber, timestamp: Date.now() });
            this.emitQueuedEvents(); // Emit any queued events
        });

        this.socket.on('reconnect_error', (error: any) => {
            this.addEventToHistory('reconnect_error', { error: error.message || error });
        });

        this.socket.on('reconnect_failed', () => {
            this.addEventToHistory('reconnect_failed', { timestamp: Date.now() });
        });

        // Re-attach existing listeners
        this.eventListeners.forEach((callbacks, event) => {
            callbacks.forEach(callback => {
                this.socket!.on(event, callback);
            });
        });
    }

    // Abstract methods that subclasses must implement
    abstract getServiceName(): string;
    abstract getEvents(): string[];
}

// ----------- Socket Service Manager -----------
class SocketServiceManager {
    private services: Map<string, BaseSocketService> = new Map();
    private globalSocket: Socket | null = null;
    private globalConfig: SocketServiceConfig | null = null;
    private isGlobalSocketConnecting: boolean = false;

    // Initialize global socket connection
    initializeGlobalSocket(config: SocketServiceConfig): void {
        if (this.globalSocket?.connected) {
            return;
        }

        if (this.isGlobalSocketConnecting) {
            return;
        }

        this.globalConfig = config;
        this.isGlobalSocketConnecting = true;
        
        try {
            // Convert HTTP URL to WebSocket URL if needed
            let socketUrl = config.url;
            if (socketUrl.startsWith('http://')) {
                socketUrl = socketUrl.replace('http://', 'ws://');
            } else if (socketUrl.startsWith('https://')) {
                socketUrl = socketUrl.replace('https://', 'wss://');
            }

            this.globalSocket = io(socketUrl, {
                path: '/ws/', // Backend expects this path
                autoConnect: config.autoConnect ?? true,
                reconnection: config.reconnection ?? true,
                reconnectionAttempts: config.reconnectionAttempts ?? 5,
                reconnectionDelay: config.reconnectionDelay ?? 1000,
                transports: ['websocket', 'polling'], // Allow fallback like backend
                timeout: 20000,
                forceNew: false, // Don't force new connection - share existing
                ...config.options
            });

            // Set up global socket event listeners
            this.setupGlobalSocketListeners();

        } catch (error) {
            this.isGlobalSocketConnecting = false;
        }
    }

    // Setup global socket event listeners
    private setupGlobalSocketListeners(): void {
        if (!this.globalSocket) return;

        this.globalSocket.on('connect', () => {
            this.isGlobalSocketConnecting = false;
        });

        this.globalSocket.on('disconnect', (reason: string) => {
            this.isGlobalSocketConnecting = false;
        });

        this.globalSocket.on('connect_error', (error: any) => {
            this.isGlobalSocketConnecting = false;
        });

        this.globalSocket.on('reconnect', (attemptNumber: number) => {
        });

        this.globalSocket.on('reconnect_error', (error: any) => {
        });

        this.globalSocket.on('reconnect_failed', () => {
            this.isGlobalSocketConnecting = false;
        });
    }

    // Register a service
    registerService(service: BaseSocketService): void {
        this.services.set(service.getServiceName(), service);
    }

    // Get a service by name
    getService<T extends BaseSocketService>(serviceName: string): T | undefined {
        return this.services.get(serviceName) as T;
    }

    // Get all services
    getAllServices(): BaseSocketService[] {
        return Array.from(this.services.values());
    }

    // Disconnect all services
    disconnectAll(): void {
        this.services.forEach(service => service.disconnect());
        if (this.globalSocket) {
            this.globalSocket.disconnect();
            this.globalSocket = null;
        }
        this.isGlobalSocketConnecting = false;
    }

    // Get global socket
    getGlobalSocket(): Socket | null {
        return this.globalSocket;
    }

    // Get global config
    getGlobalConfig(): SocketServiceConfig | null {
        return this.globalConfig;
    }

    // Check if any service is connected
    isAnyServiceConnected(): boolean {
        return this.globalSocket?.connected ?? false;
    }

    // Get connection status of all services
    getServicesConnectionStatus(): Record<string, boolean> {
        const status: Record<string, boolean> = {};
        this.services.forEach((service, name) => {
            status[name] = service.isConnected();
        });
        return status;
    }

    // Check if global socket is connecting
    getGlobalSocketConnectingStatus(): boolean {
        return this.isGlobalSocketConnecting;
    }
}

// Create singleton instance
export const socketServiceManager = new SocketServiceManager();

// Export the manager as default
export default socketServiceManager; 